import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardDateRangeControls } from '@/features/dashboard/components/DashboardDateRangeControls';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { ExecutionMetricsChartPanel } from '@/features/executions/components/ExecutionMetricsChartPanel';
import { queryTestExecutions } from '@/features/executions/data';
import { MetricValueCardsRow } from '@/features/metrics/components/MetricValueCardsRow';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { DetailDeleteHeaderAction } from '@/features/shared/components/DetailDeleteHeaderAction';
import { DetailTablePanel } from '@/features/shared/components/tableSystem/DetailTablePanel';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import {
  resolveMetaDateBounds,
  type TableQueryMeta,
} from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import {
  useCloneDateWindow,
  type CloneDateQuickFilter,
} from '@/features/shared/hooks/useCloneDateWindow';
import { TestDetailsPanels } from '@/features/tests/components/TestDetailsPanels';
import { TestExecutionsTable } from '@/features/tests/components/TestExecutionsTable';
import {
  cloneExecutionColumnOptions,
  cloneExecutionDefaultColumnVisibilityModel,
  cloneExecutionDefaultSortDirection,
  cloneExecutionDefaultSortKey,
  cloneExecutionSortFieldMap,
  cloneExecutionSortOptions,
  cloneExecutionStatusOptions,
  type CloneExecutionSortDirection,
  type CloneExecutionSortKey,
} from '@/features/tests/pages/testExecutionsTableDefinition';
import { useTagOptions } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { DetailPageTemplate, DetailTitlePrefix } from '@/ui/composites/scaffolds';
import { PageHeader } from '@/ui/layout/PageHeader';
import { Stack } from '@/ui/layout/Stack';
import { Box, type GridColumnVisibilityModel } from '@/ui/primitives';
import { Text } from '@/ui/primitives/Text';
import type { TestDetailPageViewModel } from '@/features/tests/hooks/useTestDetailPageViewModel';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  TestDetailDto,
  TestExecutionDto,
  TestExecutionStatus,
  QueryResult,
} from '@flowqa/shared';

type TestDetailContentProps = {
  test: TestDetailDto;
  viewModel: TestDetailPageViewModel;
  onDelete?: () => Promise<void> | void;
  deleteLoading?: boolean;
};

export const TestDetailContent = ({
  test,
  viewModel,
  onDelete,
  deleteLoading = false,
}: TestDetailContentProps): ReactElement => {
  const hasCustomDates = Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay);
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { tagOptions } = useTagOptions({ projectId: selectedProjectId });
  const [cloneDateQuickFilter, setCloneDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [cloneFromDate, setCloneFromDate] = useState<Dayjs | null>(null);
  const [cloneToDate, setCloneToDate] = useState<Dayjs | null>(null);
  const [debouncedCloneExecutionSearch, setDebouncedCloneExecutionSearch] = useState('');
  const [cloneExecutionColumnVisibilityModel, setCloneExecutionColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(cloneExecutionDefaultColumnVisibilityModel);
  const [cloneExecutionSortKey, setCloneExecutionSortKey] = useState<CloneExecutionSortKey>(
    cloneExecutionDefaultSortKey,
  );
  const [cloneExecutionSortDirection, setCloneExecutionSortDirection] =
    useState<CloneExecutionSortDirection>(cloneExecutionDefaultSortDirection);
  const [cloneExecutionFilterStatuses, setCloneExecutionFilterStatuses] = useState<
    TestExecutionStatus[]
  >([]);
  const [cloneExecutionFilterTags, setCloneExecutionFilterTags] = useState<string[]>([]);
  const [cloneExecutionMeta, setCloneExecutionMeta] =
    useState<TableQueryMeta<TestExecutionDto> | null>(null);

  useEffect(() => {
    const nextQuickFilter: CloneDateQuickFilter =
      viewModel.range === '14d' || viewModel.range === '30d' || viewModel.range === 'latest'
        ? viewModel.range
        : '7d';
    setCloneDateQuickFilter(nextQuickFilter);
    setCloneFromDate(viewModel.selectedStartDay ? dayjs(viewModel.selectedStartDay) : null);
    setCloneToDate(viewModel.selectedEndDay ? dayjs(viewModel.selectedEndDay) : null);
  }, [viewModel.range, viewModel.selectedEndDay, viewModel.selectedStartDay]);

  const cloneDateWindow = useCloneDateWindow({
    dateQuickFilter: cloneDateQuickFilter,
    fromDate: cloneFromDate,
    toDate: cloneToDate,
  });
  const cloneExecutionAvailableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const cloneExecutionsNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No test executions found for selected date range'
    : formatRangeWindowNoDataText('No test executions found', cloneDateQuickFilter, false);
  const cloneExecutionStatusFilterKey = useMemo(
    () =>
      [...cloneExecutionFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterStatuses],
  );
  const cloneExecutionTagFilterKey = useMemo(
    () => [...cloneExecutionFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterTags],
  );
  const cloneExecutionDateBounds = useMemo(
    () => resolveMetaDateBounds(cloneExecutionMeta, (item) => item.timestamp),
    [cloneExecutionMeta],
  );
  const cloneExecutionQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestExecutionDto>> => {
      const isLatestQuickFilter =
        cloneDateQuickFilter === 'latest' && cloneFromDate === null && cloneToDate === null;
      const baseFilters = isLatestQuickFilter
        ? input.filters.filter(
            (filter) =>
              filter.field !== 'timestamp' &&
              filter.field !== 'createdAt' &&
              filter.field !== 'isLatest',
          )
        : input.filters;
      const toolbarFilters = mergeFilters(
        buildEqFilterValue('testId', viewModel.testId),
        buildInFilter('status', cloneExecutionFilterStatuses),
        buildInFilter('tags', cloneExecutionFilterTags),
        isLatestQuickFilter
          ? buildEqFilterValue('isLatest', true)
          : buildDateRangeFilters(
              'timestamp',
              cloneDateWindow.startDay
                ? dayjs(cloneDateWindow.startDay).startOf('day').toISOString()
                : null,
              cloneDateWindow.endDay
                ? dayjs(cloneDateWindow.endDay).endOf('day').toISOString()
                : null,
            ),
      );
      const sortOverride: ApiSort = {
        field: cloneExecutionSortFieldMap[cloneExecutionSortKey],
        direction: cloneExecutionSortDirection,
      };
      return queryTestExecutions(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        search: debouncedCloneExecutionSearch,
        sorts: [sortOverride],
        filters: [...baseFilters, ...toolbarFilters],
      });
    },
    [
      cloneDateQuickFilter,
      cloneDateWindow.endDay,
      cloneDateWindow.startDay,
      cloneExecutionFilterStatuses,
      cloneExecutionFilterTags,
      cloneExecutionSortDirection,
      cloneExecutionSortKey,
      cloneFromDate,
      cloneToDate,
      debouncedCloneExecutionSearch,
      selectedOrganisationId,
      selectedProjectId,
      viewModel.testId,
    ],
  );
  const testExecutionsNoDataText = formatRangeWindowNoDataText(
    'No test executions found',
    viewModel.range,
    hasCustomDates,
  );

  return (
    <DetailPageTemplate
      title={
        <Text role="sectionTitle" component="h1">
          <DetailTitlePrefix label="Test" />
          {viewModel.title}
        </Text>
      }
      headerActions={
        <DetailDeleteHeaderAction
          onDelete={onDelete}
          deleteLoading={deleteLoading}
          dialogTitle="Delete Test"
          dialogDescription={
            <Stack gap="sm">
              <Text role="body" color="textSecondary">
                Deleting this test also removes associated test executions.
              </Text>
              <Text role="body" color="textSecondary">
                This action cannot be undone.
              </Text>
            </Stack>
          }
          confirmLabel="Delete Test"
        />
      }
      metadata={<TestDetailsPanels test={test} />}
      primaryContent={
        <Box sx={{ mt: s('md') }}>
          <Stack gap="md">
            <Box
              sx={{
                borderTop: 1,
                borderBottom: 1,
                borderColor: 'divider',
                py: s('lg'),
              }}
            >
              <PageHeader
                title="Test Executions"
                actions={
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    gap="md"
                    sx={{ alignItems: { xs: 'stretch', md: 'center' } }}
                  >
                    <TagsFilterField
                      value={viewModel.selectedTags}
                      options={viewModel.tagOptions}
                      onChange={viewModel.handleTagsChange}
                      emptyLabel={viewModel.tagOptionsLoading ? 'Loading Tags...' : ''}
                    />
                    <DashboardDateRangeControls
                      value={viewModel.range}
                      onChange={(nextRange) => {
                        viewModel.setRange(nextRange);
                        viewModel.setSelectedStartDay(null);
                        viewModel.setSelectedEndDay(null);
                      }}
                      displayStartDay={viewModel.pickerStartDay}
                      displayEndDay={viewModel.pickerEndDay}
                      isCustomRange={
                        Boolean(viewModel.selectedStartDay ?? viewModel.selectedEndDay) &&
                        !viewModel.isAllMode
                      }
                      onSelectedStartDayChange={viewModel.handleStartDayChange}
                      onSelectedEndDayChange={viewModel.handleEndDayChange}
                      fullWidthOnMobile
                    />
                  </Stack>
                }
              />
            </Box>
            <Box sx={{ mt: s('md') }}>
              <Stack gap="lg">
                <StatusMetricCardsRow cards={viewModel.testMetricCards} />
                <MetricValueCardsRow items={viewModel.metricValueItems} />
              </Stack>
            </Box>
            <Box sx={{ mt: s('md') }}>
              <ExecutionMetricsChartPanel
                chartTab={viewModel.chartTab}
                tabsAriaLabel="Test detail chart tabs"
                onChartTabChange={viewModel.setChartTab}
                showChartDateClear={viewModel.showChartDateClear}
                onChartDateClear={viewModel.handleChartDateClear}
                chartHeight={viewModel.chartHeight}
                suppressChartAnimation={viewModel.suppressChartAnimation}
                stackedChartData={viewModel.stackedChartData}
                passRateChartData={viewModel.passRateChartData}
                frequencyChartData={viewModel.frequencyChartData}
                durationChartData={viewModel.durationChartData}
                onChartSelect={viewModel.handleChartSelect}
                showLineChartPoints={viewModel.showLineChartPoints}
                emptyStateTitle={testExecutionsNoDataText}
              />
            </Box>
            <Box sx={{ mt: s('lg') }}>
              <DetailTablePanel
                tabLabel="Executions"
                tabsAriaLabel="Test detail executions panel"
                toolbarSlot={
                  <TableToolbar<CloneExecutionSortKey, TestExecutionStatus>
                    title={undefined}
                    searchAriaLabel="Search test executions"
                    onDebouncedSearchChange={setDebouncedCloneExecutionSearch}
                    columnVisibilityModel={cloneExecutionColumnVisibilityModel}
                    defaultColumnVisibilityModel={cloneExecutionDefaultColumnVisibilityModel}
                    columnOptions={cloneExecutionColumnOptions}
                    onColumnVisibilityModelChange={setCloneExecutionColumnVisibilityModel}
                    sortKey={cloneExecutionSortKey}
                    sortDirection={cloneExecutionSortDirection}
                    defaultSortKey={cloneExecutionDefaultSortKey}
                    defaultSortDirection={cloneExecutionDefaultSortDirection}
                    sortOptions={cloneExecutionSortOptions}
                    onSortChange={(nextSortKey, nextSortDirection) => {
                      setCloneExecutionSortKey(nextSortKey);
                      setCloneExecutionSortDirection(nextSortDirection);
                    }}
                    filterStatuses={cloneExecutionFilterStatuses}
                    statusOptions={cloneExecutionStatusOptions}
                    onFilterStatusesChange={setCloneExecutionFilterStatuses}
                    filterTags={cloneExecutionFilterTags}
                    onFilterTagsChange={setCloneExecutionFilterTags}
                    dateQuickFilter={cloneDateQuickFilter}
                    onDateQuickFilterChange={(next) => {
                      setCloneDateQuickFilter(next);
                      setCloneFromDate(null);
                      setCloneToDate(null);
                    }}
                    fromDate={cloneFromDate}
                    toDate={cloneToDate}
                    onFromDateChange={setCloneFromDate}
                    onToDateChange={setCloneToDate}
                    availableTags={cloneExecutionAvailableTags}
                    includeLatestQuickFilter
                    statusFilterLabel="Status"
                    summaryCount={cloneExecutionMeta?.total ?? null}
                    summarySingularLabel="test execution"
                    summaryPluralLabel="test executions"
                    previewFromDate={
                      cloneDateQuickFilter === 'latest' ? cloneExecutionDateBounds.from : null
                    }
                    previewToDate={
                      cloneDateQuickFilter === 'latest' ? cloneExecutionDateBounds.to : null
                    }
                  />
                }
              >
                <TestExecutionsTable
                  persistKey={null}
                  hideNameColumn
                  runColumnFirst
                  queryKey={[
                    'test-detail-executions-table',
                    viewModel.testId,
                    selectedOrganisationId ?? null,
                    selectedProjectId ?? null,
                    cloneDateQuickFilter,
                    cloneExecutionSortKey,
                    cloneExecutionSortDirection,
                    cloneExecutionStatusFilterKey,
                    cloneExecutionTagFilterKey,
                    debouncedCloneExecutionSearch,
                    cloneDateWindow.startDay,
                    cloneDateWindow.endDay,
                  ]}
                  queryFn={cloneExecutionQueryFn}
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialPageSize={10}
                  noRowsText={cloneExecutionsNoRowsText}
                  showToolbar={false}
                  disableColumnSorting
                  disableColumnMenu
                  search={debouncedCloneExecutionSearch}
                  onQueryMeta={setCloneExecutionMeta}
                  columnVisibilityModel={cloneExecutionColumnVisibilityModel}
                  onColumnVisibilityModelChange={(nextModel) => {
                    setCloneExecutionColumnVisibilityModel({
                      ...nextModel,
                      name: true,
                    });
                  }}
                />
              </DetailTablePanel>
            </Box>
          </Stack>
        </Box>
      }
    />
  );
};
